import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { throttle } from 'lodash';

import {
  moonScalingBreakpoints,
  moonTranslateXMap,
  mountainVerticalOffsetBreakpoints,
  craterVerticalOffsetBreakpoints,
} from './hero.constants.js';

import {
  craterImg,
  craterImgPhone,
  moonImg,
  mountain1Img,
  mountain2Img,
  mountain2ImgPhone,
  starsImg,
} from '../../assets/parallax';
import HeroCanvasLoader from './HeroCanvasLoader.jsx';

const LaptopCanvas = React.lazy(() => import('./Laptop.jsx'));

const Hero = () => {
  const { t } = useTranslation();
  const [offsetY, setOffsetY] = useState(0);
  const [moonScale, setMoonScale] = useState(1.0);
  const [mountain2Offset, setMountain2Offset] = useState('6vh');
  const [craterOffset, setCraterOffset] = useState('8vh');
  const [isCanvasVisible, setIsCanvasVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;

      const newMoonScale = moonScalingBreakpoints.find(
        (bp) => currentWidth <= bp.width
      ).scale;
      setMoonScale(newMoonScale);

      const newMountainOffset = mountainVerticalOffsetBreakpoints.find(
        (bp) => currentWidth <= bp.width
      ).offset;
      setMountain2Offset(newMountainOffset);

      const newCraterOffset = craterVerticalOffsetBreakpoints.find(
        (bp) => currentWidth <= bp.width
      ).offset;
      setCraterOffset(newCraterOffset);
    };

    handleResize();

    const throttledScroll = throttle(() => setOffsetY(window.scrollY), 100);

    window.addEventListener('scroll', throttledScroll);
    window.addEventListener('resize', handleResize);

    const idleCallbackHandle = requestIdleCallback(() => {
      setIsCanvasVisible(true);
    });

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      window.removeEventListener('resize', handleResize);
      cancelIdleCallback(idleCallbackHandle);
    };
  }, []);

  const parallaxSpeeds = {
    content: 0.16,
    stars: 0.03,
    sun: 0.05,
    mountain1: 0.065,
    mountain2: 0.08,
    crater: 0.1,
  };

  const parallaxEffect = `translateY(${offsetY * parallaxSpeeds.sun}px)`;
  const translateX = moonTranslateXMap[moonScale] || '0%';
  const moonTransform = `${parallaxEffect} translateX(${translateX}) scale(${moonScale})`;

  const mountain2Transform = `translateY(calc(${offsetY * parallaxSpeeds.mountain2}px + ${mountain2Offset}))`;
  const craterTransform = `translateY(calc(${offsetY * parallaxSpeeds.crater}px + ${craterOffset}))`;

  return (
    <section className="relative w-full h-screen overflow-hidden">
      <img
        className="absolute inset-0 w-full h-full object-cover z-[1]"
        src={starsImg || ''}
        alt="A starry night sky"
        style={{ transform: `translateY(${offsetY * parallaxSpeeds.stars}px)` }}
      />
      <img
        className="absolute inset-0 w-full h-full object-contain object-[90%_center] z-[2]"
        src={moonImg || ''}
        alt="A large moon"
        style={{ transform: moonTransform }}
        fetchpriority={'high'}
      />
      <img
        className="absolute inset-0 w-full h-full object-cover z-[3]"
        src={mountain1Img || ''}
        alt="A distant mountain"
        style={{
          transform: `translateY(calc(${offsetY * parallaxSpeeds.mountain1}px + 7vh))`,
        }}
      />
      <picture
        style={{ transform: mountain2Transform }}
        className="absolute inset-0 w-full h-full object-cover z-[4]"
      >
        <source media="(max-width: 664px)" srcSet={mountain2ImgPhone || ''} />
        <source media="(min-width: 665px)" srcSet={mountain2Img || ''} />
        <img
          src={mountain2Img || ''}
          alt="A closer mountain"
          className="w-full h-full object-cover"
        />
      </picture>
      <div
        className="absolute inset-x-0 top-[12%] text-left pl-5 pr-6 sm:top-[14%] sm:pl-10 sm:pr-10 md:pl-12
                md:pr-16 lg:top-[12%] lg:pl-18 lg:pr-24 lg:mt-6 xl:pl-32 xl:pr-32 2xl:pl-30 2xl:pr-40 xxs:mt-6 xxs:ml-1.5 z-[5]"
        style={{
          transform: `translateY(${offsetY * parallaxSpeeds.content}px)`,
        }}
      >
        <div className="flex-1 w-full lg:mb-0">
          <h1
            className="font-medium text-white text-[50px] 3xs:text-[50px] xxs:text-[60px] xs:text-[70px]
                    sm:text-[70px] md:text-[100px] lg:text-[120px] xl:text-[120px] laptop-lg:text-[140px]
                    2xl:text-[160px] 3xl:text-[200px] 4xl:text-[235px] leading-tight 3xs:leading-[60px]
                    xxs:leading-[70px] xs:leading-[70px] sm:leading-[70px] md:leading-[100px] lg:leading-[115px]
                    xl:leading-[120px] laptop-lg:leading-[120px] 2xl:leading-[135px] 3xl:leading-[180px] 4xl:leading-[240px]"
          >
            Luka <span className="inline-block"> </span>
            <br className="inline-block" />
            Vukanović
          </h1>
          <div
            className="relative font-medium text-white text-[26px] 3xs:text-[25px] xs:text-[35px]
                        sm:text-[35px] md:text-[36px] lg:text-[42px] xl:text-[56px] laptop-lg:text-[56px]
                        2xl:text-[66px] 3xl:text-[80px] 4xl:text-[85px] mt-2 sm:mt-4 lg:mt-0 h-auto flex justify-start
                        items-center leading-tight sm:leading-snug xs:leading-[50px] md:leading-[50px] lg:leading-[32px]
                        xl:leading-[36px] laptop-lg:leading-[36px] 2xl:leading-[40px] 3xl:leading-[48px] 4xl:leading-[52px]' lg:h-auto lg:w-full"
          >
            <div className="lg:absolute lg:inset-0 sm:lg:top-[10px] lg:top-[25px] flex flex-col w-full">
              <div
                className="text first lg:absolute lg:left-0 md:lg:left-0 2xl:lg:left-0 flex justify-start"
                aria-label="Software Engineer"
              >
                {t('hero.position')}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 w-full h-full z-[6]">
        {isCanvasVisible && (
          <Suspense fallback={<HeroCanvasLoader />}>
            <LaptopCanvas />
          </Suspense>
        )}
      </div>
      <picture
        style={{ transform: craterTransform }}
        className="absolute inset-0 w-full h-full object-cover z-[7]"
      >
        <source media="(max-width: 664px)" srcSet={craterImgPhone || ''} />
        <source media="(min-width: 665px)" srcSet={craterImg || ''} />
        <img
          src={craterImg || ''}
          alt="A cratered surface"
          className="w-full h-full object-cover"
        />
      </picture>
    </section>
  );
};

export default Hero;
